/**
 * Environment variable compatibility layer for CRA -> Vite migration
 *
 * This utility provides a unified way to access environment variables
 * that works with both:
 * - CRA (process.env.REACT_APP_*)
 * - Vite (import.meta.env.VITE_*)
 *
 * Usage: import { env } from 'utils/env'
 * Then use: env.APP_ENV instead of process.env.REACT_APP_ENV
 */

// Check if we're running in Vite
const isVite = typeof import.meta !== 'undefined' && import.meta.env !== undefined

/**
 * Get environment variable value, checking both Vite and CRA patterns
 */
function getEnvValue(key: string): string | undefined {
  if (isVite) {
    // Vite uses VITE_ prefix
    const viteKey = `VITE_${key}`
    const value = (import.meta.env as Record<string, string>)[viteKey]
    if (value !== undefined) return value
  }

  // Fallback to CRA pattern (process.env.REACT_APP_*)
  const craKey = `REACT_APP_${key}`
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (process.env as any)[craKey]
}

/**
 * Get NODE_ENV equivalent
 */
function getNodeEnv(): string {
  if (isVite) {
    return import.meta.env.MODE
  }
  return process.env.NODE_ENV || 'development'
}

/**
 * Check if running in development mode
 */
function isDev(): boolean {
  if (isVite) {
    return import.meta.env.DEV
  }
  return process.env.NODE_ENV === 'development'
}

/**
 * Check if running in production mode
 */
function isProd(): boolean {
  if (isVite) {
    return import.meta.env.PROD
  }
  return process.env.NODE_ENV === 'production'
}

export const env = {
  /**
   * The application environment (production, staging, on-prem, etc.)
   */
  get APP_ENV(): string | undefined {
    return getEnvValue('ENV')
  },

  /**
   * The Node environment (development, production, test)
   */
  get NODE_ENV(): string {
    return getNodeEnv()
  },

  /**
   * Whether we're in development mode
   */
  get isDevelopment(): boolean {
    return isDev()
  },

  /**
   * Whether we're in production mode
   */
  get isProduction(): boolean {
    return isProd()
  },

  /**
   * Whether we're running in Vite
   */
  get isVite(): boolean {
    return isVite
  },
}

export default env
